import { SCANNER_PRICE, type ScannerMessage } from '@hotlist/shared';

/**
 * A police scanner, bought and fitted in the car.
 *
 * ## Why this exists at all
 *
 * §4 draws a line between what a person can perceive and what the network
 * records, and this is a third thing that sits on neither side: a **public
 * radio channel**. It is not the network — a civilian cannot query it, cannot
 * search it, and gets only what happens to be broadcast while they are nearby.
 * It is not eyesight either, because it reaches two and a half miles and names a
 * plate.
 *
 * Which makes it the only way anybody outside a department finds out what a
 * plate read turned into. A player who has watched their own week fill up in
 * the flocked app can now, occasionally, hear the other end of somebody else's:
 * *vehicle of interest, plate ABC-1234, near Sussex.* That is the whole
 * argument arriving through a channel that has been public for decades, which
 * is a better way to make it than a paragraph.
 *
 * ## What it does not do
 *
 * It does not tell you where anybody is. A call names the nearest town, because
 * that is what comes over a radio, and handing out coordinates would turn a
 * receiver into a tracker — which is precisely the thing the rest of this game
 * is about not doing casually.
 *
 * And most of what it carries is nothing to do with plate readers. Speed,
 * alarms, welfare checks. That is not padding: if every call on the scanner
 * were a hotlist hit, the game would be claiming that policing is downstream of
 * the network, which is false and oddly flattering to the network.
 */

/** How many calls to keep. A scanner is a thing you overhear, not an archive. */
const KEPT = 40;

export interface ScannerHandlers {
  /**
   * Go look at where a call went out.
   *
   * As coarse as the radio itself: a scanner names a town, not a position, so
   * the map pans to the town and no closer. What the row already carries is
   * the interesting part -- a traffic stop goes out with the plate read over
   * the air, and a civilian who recognizes their own plate in this feed has
   * just learned something the game never announces.
   */
  look?: (town: string) => void;
  buy: () => void;
}

export class ScannerApp {
  private readonly body: HTMLElement;
  private readonly handlers: ScannerHandlers;
  private readonly heard: (ScannerMessage & { clock: string })[] = [];
  private owned = false;

  constructor(handlers: ScannerHandlers) {
    this.handlers = handlers;
    this.body = must('scanner-body');
    this.body.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).dataset.buy !== undefined) handlers.buy();
    });
    this.paint();
  }

  setOwned(owned: boolean): void {
    if (owned === this.owned) return;
    this.owned = owned;
    this.paint();
  }

  hear(call: ScannerMessage, clock: string): void {
    this.heard.unshift({ ...call, clock });
    if (this.heard.length > KEPT) this.heard.length = KEPT;
    this.paint();
  }

  private paint(): void {
    this.body.replaceChildren();

    if (!this.owned) {
      const sell = document.createElement('div');
      sell.className = 'app-empty';
      const said = document.createElement('p');
      said.textContent =
        'Hear units on a call within a few miles: what they were sent to, and roughly where. ' +
        'Anybody can buy one.';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'app-check';
      button.dataset.buy = '';
      button.textContent = `buy a scanner — $${SCANNER_PRICE}`;
      sell.append(said, button);
      this.body.append(sell);
      return;
    }

    if (this.heard.length === 0) {
      const quiet = document.createElement('div');
      quiet.className = 'garage-note';
      quiet.textContent = 'Quiet. Nothing within range.';
      this.body.append(quiet);
      return;
    }

    for (const call of this.heard) {
      const row = document.createElement('div');
      // A read-driven call is the one worth being able to pick out of a list,
      // because it is the only one that started with a machine.
      row.className = call.kind === 'hotlist' ? 'scanner-call hotlist' : 'scanner-call';

      const when = document.createElement('span');
      when.className = 'scanner-when';
      when.textContent = call.clock;

      const what = document.createElement('span');
      what.className = 'scanner-what';
      what.textContent = call.said;

      const where = document.createElement('span');
      where.className = 'scanner-where';
      where.textContent = call.where;

      row.classList.add('scanner-row');
      row.addEventListener('click', () => this.handlers.look?.(call.where));

      row.append(when, what, where);
      this.body.append(row);
    }
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
